<!--
//nuxt-ui/components/pages/region-squad-simulator/CardPlayerTeamSimulatorDraft.vue
-->
<script setup lang="ts">
import type { AnyDraftPlayer } from "~/src/draft/domain/models/DraftPlayer";
import { useImages } from "~/nuxt-ui/Shared/composables/images";
import { setAliasUrl } from "~/src/Shared/utils";

type Props = {
  player: AnyDraftPlayer;
  isSelected: boolean;
  isSelectable?: boolean;
  loadingLazy?: boolean;
};

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "recruit-player", player: AnyDraftPlayer): void;
}>();

const { playerPlaceholderPath } = useImages();

function handleRecruit() {
  emit("recruit-player", props.player);
}
</script>

<template>
  <div
    :class="['player-card-container cursor-pointer hover:scale-105', { '!border-primary': isSelected }]"
    @click="handleRecruit()"
  >
    <div class="h-full absolute z-[3]">
      <div
        class="clip-diagonal-right"
        :style="`background-image: linear-gradient(240deg, #88888899 40%, #141414 85%);`"
      ></div>
    </div>

    <div class="player-card-content">
      <div class="container-player-img">
        <NuxtImg
          :src="player.imageUrl ? setAliasUrl(player.imageUrl) : playerPlaceholderPath"
          :alt="player.name || ''"
          :loading="loadingLazy ? 'lazy' : 'eager'"
          height="642"
          width="456"
          sizes="122px sm:128px"
        />
        <div v-if="player.draftJersey" class="player-number">#{{ player.draftJersey }}</div>
        <div class="rounded-lg py-1 px-2 bg-primary !text-black ml-2 absolute top-2 left-0">
          <p class="text-sm font-bold">
            <span v-if="player.macros && player.macros.average"> {{ player.macros.average }} </span>
            <span v-else> - </span>
          </p>
        </div>
      </div>

      <div class="w-full min-h-full flex flex-col md:justify-between">
        <div class="container-player-info">
          <div class="container-player-info-text">
            <div>
              <h3 class="player-name">
                <span v-if="player.name">
                  {{ player.name }}
                </span>
                <span v-else>{{ $t("squadSimulator.placeholder.shortName") }} </span>
              </h3>
              <p class="player-role">
                <span v-if="player.role">
                  {{ $t(`draft.player.position.${player.role}`) }}
                </span>
                <span v-else>
                  {{ $t("squadSimulator.placeholder.role") }}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.player-card-container {
  @apply rounded-lg bg-gray-900 border-2 border-transparent overflow-hidden relative transition-all ease-in-out duration-200;
}

.clip-diagonal-right {
  @apply h-full w-[180px];
  clip-path: polygon(0 0, 100% 0%, -40% 100%, 0% 100%);
}

.player-card-content {
  @apply h-full max-w-min p-3 relative z-[5];
}

.container-player-info {
  @apply mt-2.5;
}

.player-number {
  @apply text-xs font-bold px-2 py-1 bg-gray-800 rounded-full bg-opacity-60 absolute bottom-1 right-1 z-[3];
}

.container-player-img {
  @apply shrink-0 relative rounded bg-[url('/img/bg-card/bg-card-player-image.jpg')] bg-cover bg-center bg-no-repeat min-w-36 max-w-36 aspect-square overflow-hidden;
}

.container-player-img::after {
  content: "";
  background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 51.94%);
  @apply absolute bottom-0 w-full h-20 z-[2];
}

.container-player-img img {
  @apply w-full h-full object-top object-cover;
}

.player-name {
  @apply font-bold text-xs max-w-[135px] overflow-hidden text-ellipsis whitespace-nowrap;
}

.player-role {
  @apply text-gray-400 text-xs leading-[21px] first-letter:capitalize;
}
</style>
